import { ExpandableItem } from "@atlas-int/ui";
import { text } from "@atlas-int/tokens";
import { Link } from "react-router";

const FAQContent = () => {
  return (
    <>
      <ExpandableItem
        term="What is The Atlas of Economic Complexity?"
        definition={
          <>
            <p>
              The Atlas is a research and data visualization tool that allows
              people to learn more about the economic structure of their country,
              including the growth opportunities that exist in the latent
              productive capabilities a country has.
            </p>
            <p>
              The Atlas puts the capabilities and know-how of a country at the
              heart of its growth prospects, where the diversity and complexity of
              existing capabilities heavily influence how growth happens.
            </p>
          </>
        }
      />
      <ExpandableItem
        term="What are the Atlas data sources?"
        definition={
          <>
            <p>
              Atlas data is compiled from the following international sources:
            </p>
            <ul style={{ color: text.secondary }}>
              <li>
                <b>Goods Trade (raw data):</b> United Nations Statistical Division
                (UN Comtrade)
              </li>
              <li>
                <b>Services Trade (raw data):</b> International Monetary Fund,
                Direction of Trade Statistics (DOTS)
              </li>
              <li>
                <b>Economic Indicators:</b> International Monetary Fund
              </li>
              <li>
                <b>Price Adjustments:</b> Federal Reserve Economic Data (FRED),
                Producer Price Index for Industrial Commodities
              </li>
            </ul>
            <p>
              Atlas trade values are reported in U.S. dollars (USD).
              Constant-dollar values are adjusted using the FRED Producer Price
              Index for Industrial Commodities, with the base year set to the most
              recent Atlas data year. This allows users to interpret values in
              terms of current purchasing power.
            </p>
            <p>
              For historical analysis (e.g., growth rates and long-run trends),
              constant-dollar values are recommended.
            </p>
          </>
        }
      />
      <ExpandableItem
        term="Why doesn't Atlas data match UN Comtrade data?"
        definition={
          <>
            <p>
              International trade flows are recorded twice: once by exporters and
              once by importers. In practice, these records often differ, are
              incomplete, or are reported using different product classifications.
              To address these issues, the Growth Lab developed a standardized,
              data-driven process to produce consistent and comparable bilateral
              trade estimates.
            </p>
            <p>
              Our methodology combines two core steps: reconciling partner reports
              and harmonizing product classifications over time.
            </p>
            <p>
              <b>Reconciling Exporter and Importer Reports</b>
            </p>
            <p>
              We first adjust reported values to ensure comparability between
              exporter-reported (FOB) and importer-reported (CIF) data. We then
              assess the reliability of each country's reporting based on its
              consistency across trading partners. Using these reliability scores,
              we combine exporter and importer reports into a single estimated
              trade value that places greater weight on more reliable sources.
            </p>
            <p>
              <b>Harmonizing Product Classifications</b>
            </p>
            <p>
              Countries adopt new trade classification systems at different times,
              and product codes are regularly revised, split, or merged. To ensure
              comparability across years, we use data-driven conversion weights
              that translate trade values between classification vintages while
              preserving detailed product information. This approach avoids the
              loss of products and discontinuities that arise from simple
              one-to-one mappings.
            </p>
            <p>
              By integrating reliability-weighted mirroring with weighted product
              conversion, we generate bilateral, product-level trade datasets that
              are consistent across countries and over time. These datasets form
              the core data underlying the Atlas and improve coverage, reduce
              reporting discrepancies, and support long-run analysis of global
              trade patterns.
            </p>
            <p>
              A full description of this methodology is available in our
              peer-reviewed paper:{" "}
              <a
                href="https://www.nature.com/articles/s41597-025-06488-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <em>
                  Bustos et al. (2026), Tackling Discrepancies in Trade Data: The
                  Harvard Growth Lab International Trade Datasets, Scientific
                  Data.
                </em>
              </a>
            </p>
            <p>
              A visual description of the Growth Lab's trade data methodology is
              available on our{" "}
              <Link to="/trade-data-methodology">companion website</Link>.
            </p>
          </>
        }
      />
      <ExpandableItem
        term="What is the difference between the HS and SITC datasets?"
        definition={
          <p>
            The difference is a tradeoff between time and detail. The SITC data
            goes back further in time, from 1962 onward, but does not reflect
            new products that did not exist in 1962 (e.g., cell phones). The HS
            data is only available back to 1995 but provides a more detailed
            product set. This is especially important for products introduced in
            the last two decades, such as high-tech electronic goods.
          </p>
        }
      />
      <ExpandableItem
        term="How often is the Atlas data updated?"
        definition={
          <>
            <p>
              Atlas data updates follow international trade reporting cycles and
              require substantial processing to ensure quality and consistency.
            </p>
            <p>
              <b>Annual Updates</b>
            </p>
            <p>
              Approximately 95 percent of Atlas data is updated once per year,
              typically between April and June. This reflects country reporting
              timelines to UN Comtrade, which generally require 12 to 18 months to
              reach sufficient coverage. For example, most 2024 trade data becomes
              available in the Atlas between April and June 2026.
            </p>
            <p>
              Annual releases may incorporate late or corrected country reporting
              which can lead to small revisions in historical data. This process
              improves long-run accuracy and consistency.
            </p>
            <p>
              <b>Interim Updates</b>
            </p>
            <p>
              Throughout the year, we also release interim updates that
              incorporate newly submitted country data, reflect improvements in
              data processing methods, and address minor corrections when needed.
            </p>
            <p>
              For real-time information on country-level data availability, see
              the{" "}
              <a
                href="https://comtrade.un.org/data/da"
                target="_blank"
                rel="noopener noreferrer"
              >
                UN Comtrade Data Availability Dashboard
              </a>
              .
            </p>
          </>
        }
      />
      <ExpandableItem
        term="Why are certain countries named and classified the way they are?"
        definition={
          <p>
            We use the official country names and data provided to UN Comtrade.
          </p>
        }
      />
      <ExpandableItem
        term="Why are some countries missing from Country Profiles and the complexity ranking?"
        definition={
          <>
            <p>
              While Atlas Explore includes data for all countries and territories,
              Atlas Country Profiles and Rankings are limited to countries that
              meet minimum coverage and quality standards:
            </p>
            <ul style={{ color: text.secondary }}>
              <li>Population of at least 1 million</li>
              <li>Average annual trade volume of at least $1 billion</li>
              <li>Verified GDP and export data availability</li>
              <li>Consistent and reliable trade reporting history</li>
            </ul>
          </>
        }
      />
      <ExpandableItem
        term="Can I reuse or reprint Atlas data and visualizations?"
        definition={
          <>
            <p>
              The Atlas of Economic Complexity is a freely available public
              resource. Its data and tools are widely used in research, policy
              analysis, and applied projects. We encourage reuse and adaptation,
              provided that users cite the Atlas and underlying data sources
              appropriately.
            </p>
            <p>
              Please refer to our{" "}
              <strong>
                <Link to="/data-downloads">Usage and Citation Guidelines</Link>
              </strong>{" "}
              for specific citation requirements for the platform, datasets, and
              related publications.
            </p>
          </>
        }
      />
      <ExpandableItem
        term="Where can I learn more about the ideas and methodologies behind the Atlas?"
        definition={
          <>
            <p>
              A detailed technical description of the Growth Lab's trade data
              methodology is available in our peer-reviewed paper:{" "}
              <a
                href="https://www.nature.com/articles/s41597-025-06488-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <em>
                  Bustos et al. (2026), Tackling Discrepancies in Trade Data: The
                  Harvard Growth Lab International Trade Datasets, Scientific
                  Data.
                </em>
              </a>
            </p>
            <p>
              A visual overview of the data pipeline and processing steps is
              available on our{" "}
              <Link to="/trade-data-methodology">companion website</Link>.
            </p>
            <p>
              The intellectual foundations of the Atlas are presented in{" "}
              <a
                href="https://growthlab.hks.harvard.edu/publications/atlas-economic-complexity-mapping-paths-prosperity-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>
                  The Atlas of Economic Complexity: Mapping Paths to Prosperity
                </strong>
              </a>
              , which introduces the core concepts, motivating questions, and
              analytical framework underlying the platform.
            </p>
            <p>
              These ideas are further developed in the Growth Lab's research on
              economic complexity and product relatedness, including:
            </p>
            <ul style={{ color: text.secondary }}>
              <li>
                <a
                  href="http://science.sciencemag.org/content/317/5837/482"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>The Product Space Conditions the Development of Nations</em>
                </a>
              </li>
              <li>
                <a
                  href="http://www.pnas.org/content/106/26/10570.full"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>The Building Blocks of Economic Complexity</em>
                </a>
              </li>
              <li>
                <a
                  href="https://link.springer.com/article/10.1007/s10887-011-9071-4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <em>The Network Structure of Economic Output</em>
                </a>
              </li>
            </ul>
          </>
        }
      />
      <ExpandableItem
        term="Who should I contact for media, research, or general inquiries about the Atlas?"
        definition={
          <p>
            Please contact the Growth Lab team at{" "}
            <a
              href="mailto:growthlab@hks.harvard.edu"
              target="_blank"
              rel="noopener noreferrer"
            >
              growthlab@hks.harvard.edu
            </a>
            .
          </p>
        }
      />
    </>
  );
};

export default FAQContent;
